import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import API_CONFIG from './config/api';

const exibirMoeda = (val) => {
  if (val == null || isNaN(val)) return '—';
  return 'R$ ' + Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const calcPctFipe = (preco, fipe) => {
  if (!preco || !fipe || fipe === 0) return null;
  return ((preco / fipe) * 100).toFixed(1);
};

const obterMesAtualFiltro = () => {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
};

const obterDataCalendarioISO = (valor) => {
  if (!valor) return '';
  if (typeof valor === 'string') {
    const match = valor.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '';
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
};

const obterMesCalendario = (valor) => obterDataCalendarioISO(valor).slice(0, 7);

const obterLabelMesAtual = () => {
  const hoje = new Date();
  return hoje.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
};

// ─── Ícone prancheta ────────────────────────────────────────────────────────
const IconeCotacao = () => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="1"/>
    <line x1="9" y1="12" x2="15" y2="12"/>
    <line x1="9" y1="16" x2="13" y2="16"/>
  </svg>
);

// ─── Modal Cotação ───────────────────────────────────────────────────────────
const ModalCotacao = ({ onClose }) => {
  const [veiculos, setVeiculos] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [bancoSelecionado, setBancoSelecionado] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState({});
  const [valores, setValores] = useState({});      // { [id]: { fipe_valor, [codigoBanco]: valor } }
  const [busca, setBusca] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const token = localStorage.getItem('token');

  // Extrai o valor de cotação de um veículo para um determinado banco
  const obterValorBancoVeiculo = (v, b) => {
    if (!v) return '';
    const cod = b.codigo_banco || b._id;
    const nomeLower = (b.nome || '').toLowerCase();
    
    // Checar cotacoes_bancos
    let val = null;
    if (v.cotacoes_bancos) {
      if (typeof v.cotacoes_bancos.get === 'function') {
        val = v.cotacoes_bancos.get(cod) ?? v.cotacoes_bancos.get(b._id) ?? v.cotacoes_bancos.get(b.nome);
      } else {
        val = v.cotacoes_bancos[cod] ?? v.cotacoes_bancos[b._id] ?? v.cotacoes_bancos[b.nome];
      }
    }

    if (val != null && val !== '') return String(val);

    // Checar campos legados
    if (nomeLower.includes('santander') && v.santander_valor != null) return String(v.santander_valor);
    if (nomeLower.includes('pan') && v.pan_valor != null) return String(v.pan_valor);
    if (v[cod] != null) return String(v[cod]);

    // Regra Daycoval: Cotação automática = FIPE - 20%
    if ((cod === 'bancoDaycoval' || nomeLower.includes('daycoval')) && v.fipe_valor) {
      const fipeNum = parseFloat(v.fipe_valor);
      if (!isNaN(fipeNum) && fipeNum > 0) {
        return String(Math.round(fipeNum * 0.80));
      }
    }

    return '';
  };

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [resV, resB] = await Promise.all([
        axios.get(`${API_CONFIG.API_URL}/veiculos`, {
          headers: { 'x-auth-token': token },
          params: { status: 'Disponível', limit: 500 }
        }),
        axios.get(`${API_CONFIG.API_URL}/bancos`, {
          headers: { 'x-auth-token': token },
          params: { ativos: 'true' }
        })
      ]);

      let listaBancos = [];
      if (resB.data?.success && Array.isArray(resB.data.data) && resB.data.data.length > 0) {
        listaBancos = resB.data.data;
      } else {
        // Fallback para bancos padrão
        listaBancos = [
          { _id: 'bancoSantander', codigo_banco: 'bancoSantander', nome: 'Santander' },
          { _id: 'bancoPAN', codigo_banco: 'bancoPAN', nome: 'Banco PAN' },
          { _id: 'bancoItauUnibanco', codigo_banco: 'bancoItauUnibanco', nome: 'Itaú Unibanco' },
          { _id: 'bancoBradesco', codigo_banco: 'bancoBradesco', nome: 'Bradesco' },
          { _id: 'bancoBancoVotorantim', codigo_banco: 'bancoBancoVotorantim', nome: 'Banco Votorantim' },
          { _id: 'bancoBancoSafra', codigo_banco: 'bancoBancoSafra', nome: 'Banco Safra' },
          { _id: 'bancoC6', codigo_banco: 'bancoC6', nome: 'Banco C6' },
          { _id: 'bancoDaycoval', codigo_banco: 'bancoDaycoval', nome: 'Daycoval' },
          { _id: 'bancoOmni', codigo_banco: 'bancoOmni', nome: 'Omni' },
          { _id: 'bancoDigimais', codigo_banco: 'bancoDigimais', nome: 'Digimais' }
        ];
      }
      setBancos(listaBancos);

      // Definir banco selecionado padrão se não estiver definido
      const padrao = listaBancos[0]?.codigo_banco || listaBancos[0]?._id || 'bancoSantander';
      setBancoSelecionado(prev => prev && listaBancos.some(b => (b.codigo_banco || b._id) === prev) ? prev : padrao);

      if (resV.data?.success) {
        const lista = resV.data.data || [];
        setVeiculos(lista);
        const init = {};
        lista.forEach(v => {
          const itemValores = {
            fipe_valor: v.fipe_valor != null ? String(v.fipe_valor) : '',
            santander_valor: v.santander_valor != null ? String(v.santander_valor) : '',
            pan_valor: v.pan_valor != null ? String(v.pan_valor) : ''
          };
          listaBancos.forEach(b => {
            const cod = b.codigo_banco || b._id;
            itemValores[cod] = obterValorBancoVeiculo(v, b);
          });
          init[v._id] = itemValores;
        });
        setValores(init);
      }
    } catch (e) {
      console.error('Erro ao carregar veículos e bancos:', e);
    }
    setCarregando(false);
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const bancoAtualObj = bancos.find(b => (b.codigo_banco || b._id) === bancoSelecionado) || bancos[0] || { nome: 'Banco', codigo_banco: bancoSelecionado };
  const chaveBancoAtual = bancoAtualObj.codigo_banco || bancoAtualObj._id || bancoSelecionado;

  const colunas = [
    { label: 'Veículo', field: 'veiculo' },
    { label: 'Ano', field: 'ano' },
    { label: 'Cor', field: 'cor' },
    { label: 'Placa', field: 'placa' },
    { label: 'Preço', field: 'preco' },
    { label: 'FIPE', field: 'pct_fipe' },
    { label: bancoAtualObj.nome || 'Banco', field: 'pct_banco', isBanco: true },
    { label: 'Entrada Mínima', field: 'entrada_minima' }
  ];

  const getVal = (v, field) => {
    const vals = valores[v._id] || {};
    const precoReal = v.preco_venda != null ? v.preco_venda - 1000 : 0;
    const fipeNum = parseFloat(vals.fipe_valor) || null;

    if (field === 'veiculo') return `${v.marca} ${v.modelo}`.toLowerCase();
    if (field === 'ano') return v.ano_fabricacao || 0;
    if (field === 'cor') return (v.cor || '').toLowerCase();
    if (field === 'placa') return (v.placa || '').toLowerCase();
    if (field === 'preco') return precoReal;
    if (field === 'fipe') return fipeNum || 0;
    if (field === 'pct_fipe') return (fipeNum && precoReal) ? precoReal / fipeNum : 0;

    if (field === 'pct_banco') {
      const raw = vals[chaveBancoAtual] || (chaveBancoAtual === 'bancoSantander' ? vals.santander_valor : (chaveBancoAtual === 'bancoPAN' ? vals.pan_valor : ''));
      const valNum = parseFloat(raw) || null;
      return (valNum && precoReal) ? precoReal / valNum : 0;
    }

    if (field === 'entrada_minima') {
      const raw = vals[chaveBancoAtual] || (chaveBancoAtual === 'bancoSantander' ? vals.santander_valor : (chaveBancoAtual === 'bancoPAN' ? vals.pan_valor : ''));
      const valNum = parseFloat(raw) || 0;
      const precoVenda = v.preco_venda || 0;
      if (!valNum || !precoVenda) return 0;
      return Math.max(0, precoVenda - valNum);
    }

    return 0;
  };

  const handleChange = (id, campo, valor) => {
    setValores(prev => {
      const itemAnterior = prev[id] || {};
      const item = { ...itemAnterior, [campo]: valor };

      // Se alterou a FIPE, recalcular automaticamente a cotação do Daycoval (FIPE - 20%)
      if (campo === 'fipe_valor') {
        const novoFipe = parseFloat(valor);
        const daycovalBanco = bancos.find(b => (b.codigo_banco || b._id) === 'bancoDaycoval' || (b.nome || '').toLowerCase().includes('daycoval'));
        if (daycovalBanco) {
          const daycovalCod = daycovalBanco.codigo_banco || daycovalBanco._id;
          if (!isNaN(novoFipe) && novoFipe > 0) {
            item[daycovalCod] = String(Math.round(novoFipe * 0.80));
          }
        }
      }

      return {
        ...prev,
        [id]: item
      };
    });
  };

  const salvar = async (id) => {
    setSalvando(prev => ({ ...prev, [id]: true }));
    try {
      const vVals = valores[id] || {};
      const cotacoes_bancos = {};

      bancos.forEach(b => {
        const cod = b.codigo_banco || b._id;
        const val = vVals[cod];
        cotacoes_bancos[cod] = (val !== '' && val != null) ? parseFloat(val) : null;
      });

      const santanderVal = vVals.bancoSantander !== undefined && vVals.bancoSantander !== '' ? vVals.bancoSantander : vVals.santander_valor;
      const panVal = vVals.bancoPAN !== undefined && vVals.bancoPAN !== '' ? vVals.bancoPAN : vVals.pan_valor;

      await axios.patch(
        `${API_CONFIG.API_URL}/veiculos/${id}/cotacao`,
        {
          fipe_valor: vVals.fipe_valor !== '' && vVals.fipe_valor != null ? parseFloat(vVals.fipe_valor) : null,
          santander_valor: santanderVal !== '' && santanderVal != null ? parseFloat(santanderVal) : null,
          pan_valor: panVal !== '' && panVal != null ? parseFloat(panVal) : null,
          cotacoes_bancos
        },
        { headers: { 'x-auth-token': token } }
      );
    } catch (e) {
      console.error('Erro ao salvar cotação:', e);
    }
    setSalvando(prev => ({ ...prev, [id]: false }));
  };

  const filtrados = veiculos
    .filter(v => {
      if (!busca.trim()) return true;
      const t = busca.toLowerCase();
      return [v.marca, v.modelo, v.placa, v.cor].join(' ').toLowerCase().includes(t);
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      const va = getVal(a, sortField);
      const vb = getVal(b, sortField);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const pctColor = (val) => {
    if (val == null) return null;
    const d = val - 100;
    if (d < 0) return { bg: '#f0fdf4', color: '#166534' };   // abaixo → verde
    if (d <= 10) return { bg: '#fef9c3', color: '#854d0e' };  // até +10% → amarelo
    return { bg: '#fef2f2', color: '#dc2626' };               // acima +10% → vermelho
  };

  const pctLabel = (val) => {
    if (val == null) return '—';
    const d = val - 100;
    const abs = Math.abs(d).toFixed(1).replace('.0', '');
    return d >= 0 ? `+${abs}%` : `-${abs}%`;
  };

  const renderCell = (vId, campo, numVal, pctVal) => {
    const vals = valores[vId] || {};
    return (
      <td key={campo} style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
        <div style={{ display: 'inline-flex', alignItems: 'stretch', border: '1px solid #e2e8f0', borderRadius: '7px', overflow: 'hidden', width: '170px' }}>
          <input
            type="number"
            value={vals[campo] ?? ''}
            onChange={e => handleChange(vId, campo, e.target.value)}
            onBlur={() => salvar(vId)}
            placeholder="—"
            style={{ flex: 1, padding: '6px 8px', border: 'none', fontSize: '0.82rem', outline: 'none', background: 'transparent', minWidth: 0 }}
          />
          <div style={{ width: '1px', background: '#e2e8f0', flexShrink: 0 }} />
          <div style={{
            padding: '0 6px', fontSize: '0.75rem', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center',
            minWidth: '52px', whiteSpace: 'nowrap',
            background: pctColor(pctVal)?.bg || '#f8fafc',
            color: pctColor(pctVal)?.color || '#94a3b8'
          }}>
            {pctLabel(pctVal)}
          </div>
        </div>
      </td>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#475569' }}><IconeCotacao /></span>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#1e293b' }}>Cotações</h2>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
              Comparativo FIPE e Cotação Bancária por Veículo
            </p>
          </div>
        </div>

        {/* Controles: Seletor de Banco e Busca */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: '600', color: '#475569' }}>Banco:</span>
            <select
              value={bancoSelecionado}
              onChange={e => setBancoSelecionado(e.target.value)}
              style={{
                padding: '6px 12px',
                fontSize: '0.85rem',
                fontWeight: '600',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                background: '#fff',
                color: '#1e293b',
                cursor: 'pointer',
                outline: 'none',
                minWidth: '170px'
              }}
            >
              {bancos.map(b => (
                <option key={b.codigo_banco || b._id} value={b.codigo_banco || b._id}>
                  {b.nome}
                </option>
              ))}
            </select>
          </div>

          <input
            type="text"
            placeholder="Buscar veículo, placa, cor..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.88rem', width: '220px', outline: 'none' }}
          />

          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>
      </div>

      {/* Tabela compacta */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 24px 24px' }}>
        {carregando ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>Carregando cotações e veículos...</div>
        ) : filtrados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#cbd5e1' }}>Nenhum veículo disponível.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0', position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
                {colunas.map(col => {
                  if (col.isBanco) {
                    return (
                      <th
                        key="banco_header"
                        style={{ textAlign: 'left', padding: '10px 10px', fontSize: '0.75rem', color: '#1e293b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span onClick={() => toggleSort('pct_banco')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            {bancoAtualObj.nome || 'Banco'}
                            {sortField === 'pct_banco'
                              ? <span style={{ marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                              : <span style={{ marginLeft: '4px', opacity: 0.25 }}>↕</span>}
                          </span>
                        </div>
                      </th>
                    );
                  }

                  return (
                    <th key={col.field}
                      onClick={() => toggleSort(col.field)}
                      style={{ textAlign: 'left', padding: '10px 10px', fontSize: '0.72rem', color: sortField === col.field ? '#1e293b' : '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                      {col.label}
                      {sortField === col.field
                        ? <span style={{ marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                        : <span style={{ marginLeft: '4px', opacity: 0.25 }}>↕</span>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtrados.map(v => {
                const vals = valores[v._id] || {};
                const precoReal = v.preco_venda != null ? v.preco_venda - 1000 : null;
                const fipeNum = parseFloat(vals.fipe_valor) || null;
                const pctFipe = calcPctFipe(precoReal, fipeNum);

                const rawBancoVal = vals[chaveBancoAtual] || (chaveBancoAtual === 'bancoSantander' ? vals.santander_valor : (chaveBancoAtual === 'bancoPAN' ? vals.pan_valor : ''));
                const bancoNum = parseFloat(rawBancoVal) || null;
                const pctBanco = calcPctFipe(precoReal, bancoNum);

                // Cálculo da Entrada Mínima = Valor de Venda - Cotação
                const precoVendaTotal = v.preco_venda != null ? v.preco_venda : null;
                const entradaMinima = (precoVendaTotal != null && bancoNum != null) ? Math.max(0, precoVendaTotal - bancoNum) : null;

                const isSaving = salvando[v._id];

                return (
                  <tr key={v._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 10px', fontWeight: '500', fontSize: '0.9rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                      {v.marca} {v.modelo}
                      {isSaving && <span style={{ marginLeft: '6px', fontSize: '0.7rem', color: '#16a34a' }}>salvo ✓</span>}
                    </td>
                    <td style={{ padding: '10px 10px', fontSize: '0.85rem', color: '#475569', whiteSpace: 'nowrap' }}>
                      {v.ano_fabricacao}/{v.ano_modelo}
                    </td>
                    <td style={{ padding: '10px 10px', fontSize: '0.85rem', color: '#475569', whiteSpace: 'nowrap' }}>{v.cor || '—'}</td>
                    <td style={{ padding: '10px 10px', fontSize: '0.85rem', color: '#475569', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{v.placa}</td>
                    <td style={{ padding: '10px 10px', fontSize: '0.85rem', color: '#16a34a', fontWeight: '600', whiteSpace: 'nowrap' }}>
                      {exibirMoeda(precoReal)}
                    </td>
                    {renderCell(v._id, 'fipe_valor', fipeNum, pctFipe)}
                    {renderCell(v._id, chaveBancoAtual, bancoNum, pctBanco)}
                    <td style={{
                      padding: '10px 12px',
                      fontSize: '0.88rem',
                      fontWeight: '700',
                      whiteSpace: 'nowrap',
                      color: entradaMinima === 0 ? '#16a34a' : (entradaMinima != null ? '#1d4ed8' : '#94a3b8')
                    }}>
                      {entradaMinima != null ? exibirMoeda(entradaMinima) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─── Card de ferramenta ──────────────────────────────────────────────────────
const ToolCard = ({ label, icon, onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: '150px', height: '150px', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '14px',
      background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '14px',
      cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s',
      padding: '20px', color: '#475569'
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#94a3b8'; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
  >
    {icon}
    <span style={{ fontSize: '0.88rem', fontWeight: '600', color: '#334155', textAlign: 'center' }}>{label}</span>
  </button>
);

// ─── Ícone financiamentos ────────────────────────────────────────────────────
const IconeFinanciamentos = () => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const STATUS_CORES = {
  'Em formalização':       { bg: '#fef9c3', color: '#854d0e' },
  'Enviado para pagamento': { bg: '#eff6ff', color: '#1e40af' },
  'Pago':                   { bg: '#f0fdf4', color: '#166534' },
};

const STATUS_OPTIONS = ['Em formalização', 'Enviado para pagamento', 'Pago'];

const formVazio = () => {
  const hoje = new Date();
  const dd = String(hoje.getDate()).padStart(2, '0');
  const mm = String(hoje.getMonth() + 1).padStart(2, '0');
  const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  return {
    data: `${hoje.getFullYear()}-${mm}-${dd}`,
    vendedor: user?.nome || '',
    banco: '',
    placa: '',
    veiculo: '',
    valor_financiado: '',
    percentual_retorno: '',
    plus: '',
    status: 'Em formalização',
  };
};

const parseMoedaFin = (str) => {
  if (!str) return null;
  const digits = String(str).replace(/\D/g, '');
  return digits ? parseInt(digits, 10) / 100 : null;
};

// Converte data ISO sem alterar fuso horário
const formatarDataLocal = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : d.toISOString();
  const [y, m, day] = s.split('T')[0].split('-');
  return `${day}/${m}/${y}`;
};
const dataParaInput = (d) => {
  if (!d) return '';
  return (typeof d === 'string' ? d : d.toISOString()).split('T')[0];
};

const formatMoedaFin = (raw) => {
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  const num = parseInt(digits, 10);
  const reais = Math.floor(num / 100);
  const centavos = num % 100;
  return `R$ ${reais.toLocaleString('pt-BR')},${String(centavos).padStart(2, '0')}`;
};

const exibirMoedaFin = (val) =>
  val != null ? Number(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';

// ─── Sub-modal: Novo / Editar Financiamento ──────────────────────────────────
const SubModalFinanciamento = ({ inicial, onSalvar, onFechar }) => {
  const [form, setForm] = useState(inicial || formVazio());
  const [usuarios, setUsuarios] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [veiculosEstoque, setVeiculosEstoque] = useState([]);
  const [buscaVeiculo, setBuscaVeiculo] = useState(inicial?.veiculo || '');
  const [mostrarOpcoes, setMostrarOpcoes] = useState(false);
  const [modoVeiculo, setModoVeiculo] = useState(inicial ? 'manual' : 'busca');
  const token = localStorage.getItem('token');

  useEffect(() => {
    const load = async () => {
      try {
        const [resU, resV, resB] = await Promise.all([
          axios.get(`${API_CONFIG.API_URL}/usuarios`, { headers: { 'x-auth-token': token } }),
          axios.get(`${API_CONFIG.API_URL}/veiculos`, { headers: { 'x-auth-token': token }, params: { status: 'Disponível', limit: 500 } }),
          axios.get(`${API_CONFIG.API_URL}/bancos`, { headers: { 'x-auth-token': token } })
        ]);
        if (resU.data.success) setUsuarios(resU.data.data);
        if (resV.data.success) setVeiculosEstoque(resV.data.data || []);
        if (resB.data.success) setBancos(resB.data.data || []);
      } catch (e) {}
    };
    load();
  // eslint-disable-next-line
  }, []);

  const set = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));

  // Usuarios grouped by cargo
  const porCargo = {};
  usuarios.forEach(u => {
    const c = u.cargo || 'Outros';
    if (!porCargo[c]) porCargo[c] = [];
    porCargo[c].push(u);
  });

  // Vehicle autocomplete
  const veiculosFiltrados = buscaVeiculo.trim()
    ? veiculosEstoque.filter(v => {
        const t = buscaVeiculo.toLowerCase();
        return [v.marca, v.modelo, v.placa, v.cor].join(' ').toLowerCase().includes(t);
      }).slice(0, 8)
    : [];

  const selecionarVeiculo = (v) => {
    set('veiculo', `${v.marca} ${v.modelo}`);
    set('placa', v.placa || '');
    setBuscaVeiculo(`${v.marca} ${v.modelo}`);
    setMostrarOpcoes(false);
  };

  const valido = () => form.data && form.vendedor.trim() && form.placa.trim() && form.veiculo.trim() && form.status;

  const handleSalvar = () => {
    onSalvar({
      ...form,
      valor_financiado: parseMoedaFin(form.valor_financiado),
      retorno: parseMoedaFin(form.retorno),      plus: parseMoedaFin(form.plus),    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4000, background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>
          {inicial ? 'Editar financiamento' : 'Novo financiamento'}
        </h2>
        <button onClick={onFechar} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>×</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
        <div className="field-row">
          <div className="field-group">
            <label>Data <span className="required">*</span></label>
            <input type="date" value={form.data} onChange={e => set('data', e.target.value)} />
          </div>
          <div className="field-group">
            <label>Vendedor <span className="required">*</span></label>
            <select value={form.vendedor} onChange={e => set('vendedor', e.target.value)}>
              <option value="">Selecione...</option>
              {Object.entries(porCargo).map(([cargo, users]) => (
                <optgroup key={cargo} label={cargo}>
                  {users.map(u => (
                    <option key={u._id} value={u.nome}>{u.nome}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </div>
        <div className="field-row">
          <div className="field-group">
            <label>Banco</label>
            <select value={form.banco} onChange={e => set('banco', e.target.value)}>
              <option value="">Selecione...</option>
              {bancos.map(b => <option key={b._id} value={b.nome}>{b.nome}</option>)}
            </select>
          </div>
          <div className="field-group" />
        </div>

        {/* Veículo */}
        <div className="field-row field-row-full">
          <div className="field-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span>Veículo <span className="required">*</span></span>
              <span style={{ display: 'flex', gap: '8px', fontSize: '0.78rem', fontWeight: '400' }}>
                <button type="button" onClick={() => setModoVeiculo('busca')}
                  style={{ padding: '2px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', background: modoVeiculo === 'busca' ? '#1e293b' : '#f8fafc', color: modoVeiculo === 'busca' ? '#fff' : '#64748b', cursor: 'pointer', fontSize: '0.75rem' }}>
                  Buscar no estoque
                </button>
                <button type="button" onClick={() => setModoVeiculo('manual')}
                  style={{ padding: '2px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', background: modoVeiculo === 'manual' ? '#1e293b' : '#f8fafc', color: modoVeiculo === 'manual' ? '#fff' : '#64748b', cursor: 'pointer', fontSize: '0.75rem' }}>
                  Manual
                </button>
              </span>
            </label>
            {modoVeiculo === 'busca' ? (
              <div style={{ position: 'relative' }}>
                <input type="text" value={buscaVeiculo}
                  onChange={e => { setBuscaVeiculo(e.target.value); setMostrarOpcoes(true); if (!e.target.value) { set('veiculo', ''); set('placa', ''); } }}
                  onFocus={() => setMostrarOpcoes(true)}
                  onBlur={() => setTimeout(() => setMostrarOpcoes(false), 200)}
                  placeholder="Buscar marca, modelo, placa..." />
                {mostrarOpcoes && veiculosFiltrados.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 5000, maxHeight: '220px', overflowY: 'auto' }}>
                    {veiculosFiltrados.map(v => (
                      <div key={v._id} onMouseDown={() => selecionarVeiculo(v)}
                        style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '0.88rem' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <span style={{ fontWeight: '600' }}>{v.marca} {v.modelo}</span>
                        <span style={{ color: '#94a3b8', marginLeft: '8px', fontFamily: 'monospace', fontSize: '0.8rem' }}>{v.placa}</span>
                        <span style={{ color: '#94a3b8', marginLeft: '8px', fontSize: '0.8rem' }}>{v.ano_fabricacao}/{v.ano_modelo} · {v.cor}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="field-row" style={{ marginTop: 0 }}>
                <div className="field-group">
                  <input type="text" value={form.veiculo} onChange={e => set('veiculo', e.target.value)} placeholder="Marca Modelo" />
                </div>
                <div className="field-group">
                  <label>Placa <span className="required">*</span></label>
                  <input type="text" value={form.placa} onChange={e => set('placa', e.target.value.toUpperCase())} placeholder="ABC-1234" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Placa (modo busca) */}
        {modoVeiculo === 'busca' && (
          <div className="field-row">
            <div className="field-group">
              <label>Placa <span className="required">*</span></label>
              <input type="text" value={form.placa} onChange={e => set('placa', e.target.value.toUpperCase())} placeholder="Preenchida ao selecionar" />
            </div>
            <div className="field-group" />
          </div>
        )}

        <div className="field-row">
          <div className="field-group">
            <label>Valor financiado</label>
            <input type="text" value={form.valor_financiado} onChange={e => set('valor_financiado', formatMoedaFin(e.target.value))} placeholder="R$ 0,00" />
          </div>
          <div className="field-group">
            <label>% Retorno</label>
            <input type="number" step="0.1" min="0" value={form.percentual_retorno}
              onChange={e => set('percentual_retorno', e.target.value)} placeholder="Ex: 3.6" />
            {(() => {
              const vf = parseMoedaFin(form.valor_financiado);
              const pct = parseFloat(form.percentual_retorno);
              if (!vf || !pct) return null;
              const ret = vf * (pct / 100);
              const liq = ret * 0.70;
              return (
                <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px', display: 'block' }}>
                  Retorno: <strong>{exibirMoedaFin(ret)}</strong>  |  Líquido: <strong style={{ color: '#16a34a' }}>{exibirMoedaFin(liq)}</strong>
                </span>
              );
            })()}
          </div>
          <div className="field-group">
            <label>Plus</label>
            <input type="text" value={form.plus} onChange={e => set('plus', formatMoedaFin(e.target.value))} placeholder="R$ 0,00" />
          </div>
        </div>
        <div className="field-row">
          <div className="field-group">
            <label>Status <span className="required">*</span></label>
            <select value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button type="button" className="btn-secondary" onClick={onFechar}>Cancelar</button>
        <button type="button" className="btn-primary" onClick={handleSalvar}
          disabled={!valido()} style={!valido() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}>
          {inicial ? 'Salvar alterações' : 'Adicionar'}
        </button>
      </div>
    </div>
  );
};

// ─── Modal principal: Financiamentos ─────────────────────────────────────────
const ModalFinanciamentos = ({ onClose }) => {
  const [lista, setLista] = useState([]);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  const [erroPDF, setErroPDF] = useState('');
  const gerandoPDFRef = useRef(false);
  const [carregando, setCarregando] = useState(true);
  const [subModal, setSubModal] = useState(null);
  const [mesFiltro, setMesFiltro] = useState(() => obterLabelMesAtual());
  const [bancoFiltro, setBancoFiltro] = useState('');
  const [sortField, setSortField] = useState('data');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (f) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } };
  const token = localStorage.getItem('token');

  const normalizarBanco = (b) => `${b || ''}`.trim().toLowerCase();

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await axios.get(`${API_CONFIG.API_URL}/financiamentos`, { headers: { 'x-auth-token': token } });
      if (res.data.success) setLista(res.data.data);
    } catch (e) {}
    setCarregando(false);
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (dados) => {
    try {
      if (subModal?.modo === 'editar') {
        const finAtualizado = (await axios.put(`${API_CONFIG.API_URL}/financiamentos/${subModal.item._id}`, dados, { headers: { 'x-auth-token': token } })).data.data;
        // Reverse-sync: se financiamento veio de uma negociação, atualiza o recebimento lá
        if (finAtualizado?.negociacao_id) {
          try {
            const resNeg = await axios.get(`${API_CONFIG.API_URL}/negociacoes/${finAtualizado.negociacao_id}`, { headers: { 'x-auth-token': token } });
            if (resNeg.data.success) {
              const neg = resNeg.data.data;
              const recAtualizado = (neg.recebimentos || []).map(r =>
                r.financiamento_id === subModal.item._id
                  ? { ...r, banco: dados.banco || r.banco, valor: dados.valor_financiado != null ? `R$ ${Number(dados.valor_financiado).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : r.valor, percentual_retorno: dados.percentual_retorno != null ? String(dados.percentual_retorno) : r.percentual_retorno }
                  : r
              );
              await axios.put(`${API_CONFIG.API_URL}/negociacoes/${finAtualizado.negociacao_id}`, { recebimentos: recAtualizado }, { headers: { 'x-auth-token': token } });
            }
          } catch (e) {}
        }
      } else {
        await axios.post(`${API_CONFIG.API_URL}/financiamentos`, dados, { headers: { 'x-auth-token': token } });
      }
      setSubModal(null);
      carregar();
    } catch (e) {}
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este financiamento?')) return;
    try {
      await axios.delete(`${API_CONFIG.API_URL}/financiamentos/${id}`, { headers: { 'x-auth-token': token } });
      carregar();
    } catch (e) {}
  };

  const exportarExcelMes = (mes, listaM) => {
    const dados = listaM.map(f => {
      const retornoLiq = f.percentual_retorno && f.valor_financiado
        ? f.valor_financiado * (f.percentual_retorno / 100) * 0.70
        : 0;
      return {
        'Data': formatarDataLocal(f.data),
        'Vendedor': f.vendedor || '',
        'Cliente': f.cliente_nome || '',
        'CPF': f.cliente_cpf || '',
        'Banco': f.banco || '',
        'Placa': f.placa || '',
        'Veículo': f.veiculo || '',
        'Valor financiado': f.valor_financiado || 0,
        'Retorno (%)': f.percentual_retorno || 0,
        'Retorno líq.': Number(retornoLiq.toFixed(2)),
        'Plus': f.plus || 0,
        'Status': f.status || ''
      };
    });
    const ws = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Financiamentos');
    XLSX.writeFile(wb, `financiamentos_${mes.replace(/\s+/g, '_')}.xlsx`);
  };

  const ordenarFinanciamentos = itens => [...itens].sort((a, b) => {
    const valor = f => {
      if (sortField === 'data') return f.data;
      if (sortField === 'retorno_liq') return (f.valor_financiado || 0) * (f.percentual_retorno || 0) / 100 * 0.7;
      if (['valor_financiado', 'percentual_retorno', 'plus'].includes(sortField)) return f[sortField] || 0;
      return String(f[sortField] || '').toLowerCase();
    };
    const va = valor(a), vb = valor(b);
    return va < vb ? (sortDir === 'asc' ? -1 : 1) : va > vb ? (sortDir === 'asc' ? 1 : -1) : 0;
  });

  const exportarPDFMes = async (mes, itens) => {
    if (gerandoPDFRef.current || !itens.length) return;
    gerandoPDFRef.current = true;
    setGerandoPDF(true);
    setErroPDF('');
    try {
      const resposta = await axios.post(`${API_CONFIG.API_URL}/financiamentos/exportar/pdf`, {
        ids: ordenarFinanciamentos(itens).map(item => item._id), periodo: mes, banco: bancoFiltro
      }, { headers: { 'x-auth-token': token }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resposta.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `financiamentos_${mes.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      let mensagem = error.response?.data?.message;
      if (error.response?.data instanceof Blob) {
        try { mensagem = JSON.parse(await error.response.data.text()).message; } catch (_) {}
      }
      setErroPDF(mensagem || 'Não foi possível exportar o PDF. Tente novamente.');
    } finally { gerandoPDFRef.current = false; setGerandoPDF(false); }
  };

  // Agrupar por mês — lê direto da string ISO para evitar bug de fuso.
  // O filtro de banco não pode alterar a lista de períodos disponíveis.
  const MESES_PT = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
  const porMesTodos = {};
  lista.forEach(f => {
    if (!f.data) return;
    const s = obterDataCalendarioISO(f.data);
    const [year, month] = s.split('T')[0].split('-').map(Number);
    const key = `${MESES_PT[month - 1]} de ${year}`;
    if (!porMesTodos[key]) porMesTodos[key] = [];
    porMesTodos[key].push(f);
  });
  const todosMeses = Array.from(new Set([obterLabelMesAtual(), ...Object.keys(porMesTodos)]));
  const filtrarBanco = (itens) => bancoFiltro
    ? itens.filter(f => normalizarBanco(f.banco) === normalizarBanco(bancoFiltro))
    : itens;
  const porMes = mesFiltro
    ? { [mesFiltro]: filtrarBanco(porMesTodos[mesFiltro] || []) }
    : Object.fromEntries(Object.entries(porMesTodos).map(([mes, itens]) => [mes, filtrarBanco(itens)]));
  // Mantém o mês selecionado mesmo sem lançamentos para o banco escolhido.
  const meses = mesFiltro ? [mesFiltro] : Object.keys(porMes);

  const bancosDisponiveis = Array.from(new Set(
    lista
      .map(f => (f.banco || '').trim())
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const baseShareEscopo = lista.filter(f => {
    if (!f.data) return false;
    if (!mesFiltro) return true;
    const s = typeof f.data === 'string' ? f.data : new Date(f.data).toISOString();
    const [year, month] = s.split('T')[0].split('-').map(Number);
    const key = `${MESES_PT[month - 1]} de ${year}`;
    return key === mesFiltro;
  });

  const baseShareBanco = bancoFiltro
    ? baseShareEscopo.filter(f => normalizarBanco(f.banco) === normalizarBanco(bancoFiltro))
    : [];

  const totalFinanciadoMercado = baseShareEscopo.reduce((s, f) => s + (f.valor_financiado || 0), 0);
  const totalFinanciadoBanco = baseShareBanco.reduce((s, f) => s + (f.valor_financiado || 0), 0);
  const shareValorBanco = bancoFiltro
    ? (totalFinanciadoMercado > 0 ? (totalFinanciadoBanco / totalFinanciadoMercado) * 100 : 0)
    : null;
  const shareQuantidadeBanco = bancoFiltro
    ? (baseShareEscopo.length > 0 ? (baseShareBanco.length / baseShareEscopo.length) * 100 : 0)
    : null;

  const colunas = ['Data', 'Vendedor', 'Cliente', 'CPF', 'Banco', 'Placa', 'Veículo', 'Valor financiado', 'Retorno', 'Retorno líq.', 'Plus', 'Status', ''];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#475569' }}><IconeFinanciamentos /></span>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#1e293b' }}>Financiamentos</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select value={bancoFiltro} onChange={e => setBancoFiltro(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', color: '#475569', background: '#f8fafc', cursor: 'pointer', minWidth: '170px' }}>
            <option value=''>Todos os bancos</option>
            {bancosDisponiveis.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={mesFiltro} onChange={e => setMesFiltro(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', color: '#475569', background: '#f8fafc', cursor: 'pointer' }}>
            {todosMeses.map(m => <option key={m} value={m} style={{ textTransform: 'capitalize' }}>{m}</option>)}
          </select>
          <button className="btn-primary"
            style={{ padding: '8px 18px', fontSize: '0.88rem' }}
            onClick={() => setSubModal({ modo: 'novo' })}>
            + Novo financiamento
          </button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>
      </div>

      {/* Conteúdo */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {erroPDF && <p role="alert" style={{ color: '#b91c1c', background: '#fef2f2', padding: 12, borderRadius: 8 }}>{erroPDF}</p>}
        {bancoFiltro && (
          <div style={{ marginBottom: '14px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', border: '1px solid #dbeafe', borderRadius: '10px', background: '#f8fbff' }}>
            <span style={{ fontSize: '0.8rem', color: '#1e3a8a', fontWeight: '700' }}>
              Share do banco {bancoFiltro}
            </span>
            <span style={{ fontSize: '0.8rem', color: '#475569' }}>
              Valor: <strong style={{ color: '#1d4ed8' }}>{shareValorBanco != null ? `${shareValorBanco.toFixed(1)}%` : '—'}</strong>
            </span>
            <span style={{ fontSize: '0.8rem', color: '#475569' }}>
              Quantidade: <strong style={{ color: '#1d4ed8' }}>{shareQuantidadeBanco != null ? `${shareQuantidadeBanco.toFixed(1)}%` : '—'}</strong>
            </span>
            {baseShareBanco.length === 0 && (
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>0 financiamentos no mês selecionado</span>
            )}
          </div>
        )}
        {carregando ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>Carregando...</div>
        ) : meses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', border: '2px dashed #e2e8f0', borderRadius: '8px', color: '#cbd5e1', marginTop: '1rem' }}>
            Nenhum financiamento registrado.
          </div>
        ) : meses.map(mes => {
          const listaM = porMes[mes];
          const totalFinanciado = listaM.reduce((s, f) => s + (f.valor_financiado || 0), 0);
          const totalRetornoLiq = listaM.reduce((s, f) => {
            const ret = f.percentual_retorno && f.valor_financiado ? f.valor_financiado * (f.percentual_retorno / 100) * 0.70 : 0;
            return s + ret;
          }, 0);
          const totalPlus = listaM.reduce((s, f) => s + (f.plus || 0), 0);
          return (
          <div key={mes} style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#475569', textTransform: 'capitalize', letterSpacing: '0.03em' }}>
                {mes}
              </h3>
              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                Financiado: <strong style={{ color: '#16a34a' }}>{exibirMoedaFin(totalFinanciado)}</strong>
              </span>
              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                Retorno líq.: <strong style={{ color: '#2563eb' }}>{exibirMoedaFin(totalRetornoLiq)}</strong>
              </span>
              {totalPlus > 0 && (
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  Plus: <strong style={{ color: '#7c3aed' }}>{exibirMoedaFin(totalPlus)}</strong>
                </span>
              )}
              <button
                onClick={() => exportarExcelMes(mes, listaM)}
                style={{
                  marginLeft: 'auto', padding: '6px 12px', fontSize: '0.78rem', fontWeight: '600',
                  color: '#166534', background: '#dcfce7', border: '1px solid #86efac',
                  borderRadius: '6px', cursor: 'pointer'
                }}
              >
                Exportar Excel
              </button>
              <button type="button" onClick={() => exportarPDFMes(mes, listaM)} disabled={gerandoPDF || listaM.length === 0}
                title="Exportar lista filtrada em PDF A4 horizontal"
                style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700, color: '#1e40af', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, cursor: gerandoPDF || !listaM.length ? 'not-allowed' : 'pointer', opacity: gerandoPDF || !listaM.length ? 0.6 : 1 }}>
                {gerandoPDF ? 'Gerando PDF…' : 'PDF'}
              </button>
            </div>
            <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    {colunas.map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: '0.72rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {listaM.length === 0 ? (
                    <tr>
                      <td colSpan={colunas.length} style={{ padding: '22px 14px', textAlign: 'center', color: '#64748b', fontSize: '0.85rem' }}>
                        0 financiamentos neste mês para o filtro selecionado.
                      </td>
                    </tr>
                  ) : ordenarFinanciamentos(listaM).map(f => (
                    <tr key={f._id} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'default' }}
                      onDoubleClick={() => {
                        const texto = [
                          `*${formatarDataLocal(f.data)}*`,
                          `Vendedor: ${f.vendedor || ''}`,
                          `Banco: ${f.banco || ''}`,
                          `Placa: ${f.placa || ''}`,
                          f.veiculo || '',
                          '',
                          f.valor_financiado != null ? `Financiado: ${Number(f.valor_financiado).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : '',
                          f.percentual_retorno != null ? `Retorno ${f.percentual_retorno}%` : '',
                          '',
                          (f.status || '').toUpperCase()
                        ].filter((l, i, arr) => !(l === '' && arr[i - 1] === '')).join('\n');
                        navigator.clipboard.writeText(texto).catch(() => {});
                      }}
                      title="Duplo clique para copiar mensagem WhatsApp">
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', color: '#475569', whiteSpace: 'nowrap' }}>
                        {formatarDataLocal(f.data)}
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', color: '#334155' }}>{f.vendedor || '—'}</td>
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', color: '#334155' }}>{f.cliente_nome || '—'}</td>
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', fontFamily: 'monospace', color: '#475569' }}>{f.cliente_cpf || '—'}</td>
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', color: '#475569' }}>{f.banco || '—'}</td>
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', fontFamily: 'monospace', color: '#475569' }}>{f.placa || '—'}</td>
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', color: '#334155' }}>{f.veiculo || '—'}</td>
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', fontWeight: '600', color: '#16a34a', whiteSpace: 'nowrap' }}>
                        {exibirMoedaFin(f.valor_financiado)}
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', color: '#475569', whiteSpace: 'nowrap' }}>
                        {f.percentual_retorno != null ? `${f.percentual_retorno}%` : '—'}
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', fontWeight: '600', color: '#16a34a', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const ret = f.percentual_retorno && f.valor_financiado ? f.valor_financiado * (f.percentual_retorno / 100) * 0.70 : null;
                          return ret ? exibirMoedaFin(ret) : '—';
                        })()}
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: '0.85rem', color: '#475569', whiteSpace: 'nowrap' }}>
                        {exibirMoedaFin(f.plus)}
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{
                          padding: '3px 9px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
                          background: STATUS_CORES[f.status]?.bg || '#f1f5f9',
                          color: STATUS_CORES[f.status]?.color || '#64748b'
                        }}>{f.status}</span>
                      </td>
                      <td style={{ padding: '8px 14px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button type="button"
                            style={{ padding: '4px 10px', fontSize: '0.78rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '5px', cursor: 'pointer', color: '#475569' }}
                            onClick={() => setSubModal({ modo: 'editar', item: { ...f, banco: f.banco || '', plus: f.plus != null ? formatMoedaFin(String(Math.round(f.plus * 100))) : '', percentual_retorno: f.percentual_retorno != null ? String(f.percentual_retorno) : '', valor_financiado: f.valor_financiado != null ? formatMoedaFin(String(Math.round(f.valor_financiado * 100))) : '', data: dataParaInput(f.data) } })}>
                            ✏️
                          </button>
                          <button type="button"
                            style={{ padding: '4px 10px', fontSize: '0.78rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '5px', cursor: 'pointer', color: '#dc2626' }}
                            onClick={() => excluir(f._id)}>
                            ×
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          );
        })}
      </div>

      {subModal && (
        <SubModalFinanciamento
          inicial={subModal.modo === 'editar' ? subModal.item : null}
          onSalvar={salvar}
          onFechar={() => setSubModal(null)}
        />
      )}
    </div>
  );
};

// ─── Ícone Metas ─────────────────────────────────────────────────────────────
const IconeMetas = () => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="6"/>
    <circle cx="12" cy="12" r="2"/>
  </svg>
);

const IconeRanking = () => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 21V10" />
    <path d="M16 21V3" />
    <path d="M12 21v-6" />
    <path d="M4 21h16" />
  </svg>
);

const ModalRanking = ({ onClose }) => {
  const [negociacoes, setNegociacoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mesFiltro, setMesFiltro] = useState(() => obterMesAtualFiltro());
  const token = localStorage.getItem('token');

  useEffect(() => {
    const carregar = async () => {
      setCarregando(true);
      try {
        const res = await axios.get(`${API_CONFIG.API_URL}/negociacoes`, {
          headers: { 'x-auth-token': token }
        });
        if (res.data.success) setNegociacoes(res.data.data || []);
      } catch (e) {}
      setCarregando(false);
    };
    carregar();
  }, [token]);

  const rankingVendedores = (() => {
    const base = negociacoes.filter(neg => {
      if (!mesFiltro) return true;
      return obterMesCalendario(neg.data) === mesFiltro;
    });

    const contagem = {};
    base.forEach(neg => {
      const vendedor = (neg.vendedor_responsavel || 'Sem vendedor').trim();
      if (!vendedor) return;
      contagem[vendedor] = (contagem[vendedor] || 0) + 1;
    });

    return Object.entries(contagem)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([vendedor, qtd]) => ({ vendedor, qtd }))
      .slice(0, 10);
  })();

  const todosMeses = (() => {
    const mesAtual = obterMesAtualFiltro();
    const dataMesAtual = new Date(`${mesAtual}-01T00:00:00`);
    const vistos = new Set([mesAtual]);
    const lista = [{
      val: mesAtual,
      label: dataMesAtual.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    }];
    negociacoes.forEach(neg => {
      const dataISO = obterDataCalendarioISO(neg.data);
      if (!dataISO) return;
      const val = dataISO.slice(0, 7);
      const [ano, mes] = val.split('-').map(Number);
      const label = new Date(ano, mes - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      if (!vistos.has(val)) {
        vistos.add(val);
        lista.push({ val, label });
      }
    });
    lista.sort((a, b) => b.val.localeCompare(a.val));
    return lista;
  })();

  const top3 = rankingVendedores.slice(0, 3);
  const restante = rankingVendedores.slice(3);
  const maxVendasBarra = rankingVendedores.some(v => v.qtd >= 15) ? 20 : 15;

  const paleta = [
    ['#0ea5e9', '#0369a1'],
    ['#22c55e', '#15803d'],
    ['#f59e0b', '#b45309'],
    ['#ec4899', '#be185d'],
    ['#8b5cf6', '#6d28d9'],
    ['#14b8a6', '#0f766e']
  ];

  const montarIniciais = (nome) => {
    if (!nome) return 'SV';
    const partes = nome.trim().split(/\s+/).filter(Boolean);
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
    return `${partes[0][0] || ''}${partes[1][0] || ''}`.toUpperCase();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#475569' }}><IconeRanking /></span>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#1e293b' }}>Ranking</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <select value={mesFiltro} onChange={e => setMesFiltro(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', color: '#475569', background: '#f8fafc', textTransform: 'capitalize' }}>
            {todosMeses.map(o => <option key={o.val} value={o.val} style={{ textTransform: 'capitalize' }}>{o.label}</option>)}
          </select>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {carregando ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>Carregando...</div>
        ) : (
          <div style={{ padding: '18px', borderRadius: '16px', border: '1px solid #e2e8f0', background: 'radial-gradient(circle at 0% 0%, #f0f9ff 0%, #ffffff 38%), radial-gradient(circle at 100% 100%, #eef2ff 0%, #ffffff 34%)' }}>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '0.76rem', fontWeight: '800', color: '#0f766e', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Painel de Performance</div>
              <div style={{ fontSize: '1.02rem', fontWeight: '800', color: '#0f172a' }}>Top vendedores do período</div>
            </div>

            {rankingVendedores.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: '0.84rem' }}>Ainda não há vendas registradas para este filtro.</div>
            ) : (
              <>
                <div style={{ marginBottom: '16px', borderRadius: '14px', border: '1px solid #e2e8f0', background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)', padding: '16px' }}>
                  <div style={{ fontSize: '0.74rem', fontWeight: '700', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Pódio</div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    {[top3[1], top3[0], top3[2]].filter(Boolean).map((entry, idx) => {
                      const posicaoReal = entry === top3[0] ? 1 : entry === top3[1] ? 2 : 3;
                      const alturas = { 1: 132, 2: 108, 3: 88 };
                      const medalhas = { 1: '🥇', 2: '🥈', 3: '🥉' };
                      const blocoCor = posicaoReal === 1 ? '#fde68a' : posicaoReal === 2 ? '#e2e8f0' : '#fed7aa';

                      return (
                        <div key={`${entry.vendedor}-podio-${idx}`} style={{ width: '170px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <div style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '0.74rem', fontWeight: '800', background: '#0f172a', color: '#f8fafc' }}>
                            {entry.qtd} vendas
                          </div>
                          <div style={{ width: '100%', height: `${alturas[posicaoReal]}px`, borderRadius: '12px 12px 0 0', background: blocoCor, border: '1px solid #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: '8px', textAlign: 'center', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)' }}>
                            <div style={{ fontSize: '1.2rem', marginBottom: '4px' }}>{medalhas[posicaoReal]}</div>
                            <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                              {entry.vendedor}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: '2px' }}>#{posicaoReal}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ borderRadius: '14px', border: '1px solid #e2e8f0', background: '#ffffff', padding: '10px 12px' }}>
                  <div style={{ fontSize: '0.74rem', fontWeight: '700', color: '#0f766e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Classificação completa</div>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {rankingVendedores.map((entry, index) => {
                      const [c1, c2] = paleta[index % paleta.length];
                      const pct = Math.min(100, Math.round((entry.qtd / maxVendasBarra) * 100));
                      return (
                        <div key={entry.vendedor} style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '10px 12px', background: '#fff' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '34px 44px 1fr auto', alignItems: 'center', gap: '10px' }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: '800', color: '#64748b' }}>#{index + 1}</div>
                            <div style={{ width: '36px', height: '36px', borderRadius: '999px', background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`, color: '#fff', fontSize: '0.72rem', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(15,23,42,0.12)' }}>
                              {montarIniciais(entry.vendedor)}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '0.86rem', fontWeight: '700', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.vendedor}</div>
                              <div style={{ marginTop: '6px', height: '8px', borderRadius: '999px', background: '#e5e7eb', overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${c1} 0%, ${c2} 100%)`, borderRadius: '999px' }} />
                              </div>
                            </div>
                            <div style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '800', color: '#0f172a', background: '#f1f5f9', border: '1px solid #e2e8f0' }}>{entry.qtd}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Sub-modal: Nova / Editar Meta ────────────────────────────────────────────
const SubModalMeta = ({ inicial, mesPadrao, onSalvar, onFechar }) => {
  const [form, setForm] = useState(inicial || { banco: '', mes_ano: mesPadrao || '', meta_valor: '', meta_quantidade: '', observacao: '' });
  const [bancos, setBancos] = useState([]);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API_CONFIG.API_URL}/bancos`, { headers: { 'x-auth-token': token } })
      .then(r => { if (r.data.success) setBancos(r.data.data || []); })
      .catch(() => {});
  // eslint-disable-next-line
  }, []);

  const set = (c, v) => setForm(p => ({ ...p, [c]: v }));
  const valido = () => form.banco && (!!String(form.meta_valor).replace(/\D/g, '') || Number(form.meta_quantidade) > 0);

  const handleSalvar = () => {
    onSalvar({
      ...form,
      meta_valor: form.meta_valor ? (parseMoedaFin(String(form.meta_valor)) ?? null) : null,
      meta_quantidade: form.meta_quantidade ? Number(form.meta_quantidade) : null,
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4000, background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>
          {inicial ? 'Editar meta' : 'Nova meta'}
        </h2>
        <button onClick={onFechar} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>×</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
        <div className="field-row">
          <div className="field-group">
            <label>Banco <span className="required">*</span></label>
            <select value={form.banco} onChange={e => set('banco', e.target.value)}>
              <option value="">Selecione...</option>
              {bancos.map(b => <option key={b._id} value={b.nome}>{b.nome}</option>)}
            </select>
          </div>
          <div className="field-group">
            <label>Mês de referência</label>
            <input type="month" value={form.mes_ano} onChange={e => set('mes_ano', e.target.value)} />
          </div>
        </div>
        <div className="field-row">
          <div className="field-group">
            <label>Meta de valor (R$)</label>
            <input type="text" value={form.meta_valor}
              onChange={e => set('meta_valor', formatMoedaFin(e.target.value))}
              placeholder="Opcional — ex: R$ 500.000" />
          </div>
          <div className="field-group">
            <label>Meta de quantidade (fichas)</label>
            <input type="number" min="1" value={form.meta_quantidade}
              onChange={e => set('meta_quantidade', e.target.value)}
              placeholder="Opcional — ex: 10" />
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '4px' }}>Preencha um ou ambos os campos. Quando ambos estiverem preenchidos, a meta exige os dois critérios.</p>
        <div className="field-row field-row-full">
          <div className="field-group">
            <label>Observação / Recompensa</label>
            <textarea value={form.observacao} onChange={e => set('observacao', e.target.value)}
              placeholder="Ex: Bonificação de R$ 500 ao atingir a meta"
              style={{ resize: 'vertical', minHeight: '80px', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.9rem', fontFamily: 'inherit', width: '100%' }} />
          </div>
        </div>
      </div>
      <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button type="button" className="btn-secondary" onClick={onFechar}>Cancelar</button>
        <button type="button" className="btn-primary" onClick={handleSalvar}
          disabled={!valido()} style={!valido() ? { opacity: 0.4, cursor: 'not-allowed' } : {}}>
          {inicial ? 'Salvar' : 'Criar meta'}
        </button>
      </div>
    </div>
  );
};

// ─── ProgressBar ──────────────────────────────────────────────────────────────
const ProgressBar = ({ pct }) => {
  const clamped = Math.min(pct, 100);
  const cor = pct >= 100 ? '#16a34a' : pct >= 80 ? '#2563eb' : pct >= 50 ? '#ca8a04' : '#dc2626';
  return (
    <div style={{ marginTop: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>
        <span>{pct.toFixed(1)}%</span>
        {pct >= 100 && <span style={{ color: '#16a34a', fontWeight: '700' }}>✓ Meta atingida!</span>}
      </div>
      <div style={{ background: '#e2e8f0', borderRadius: '99px', height: '10px', overflow: 'hidden' }}>
        <div style={{ width: `${clamped}%`, height: '100%', background: cor, borderRadius: '99px', transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
};

// Barra de ritmo: centro = em dia, esquerda = atrasado, direita = adiantado
const PaceBar = ({ actualPct, expectedPct }) => {
  const pace = actualPct - expectedPct;
  const isOnTrack = Math.abs(pace) < 2;
  const isAhead = pace >= 2;
  const cor = isOnTrack ? '#64748b' : isAhead ? '#16a34a' : '#dc2626';
  const fillPct = Math.min(Math.abs(pace) / 50, 1) * 50;
  const label = isOnTrack
    ? '≈ Em dia com a meta'
    : isAhead
    ? `▲ ${pace.toFixed(1)}% adiantado`
    : `▼ ${Math.abs(pace).toFixed(1)}% atrasado`;
  return (
    <div style={{ marginTop: '6px' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: '600', color: cor, textAlign: 'center', marginBottom: '3px' }}>{label}</div>
      <div style={{ position: 'relative', height: '6px', background: '#f1f5f9', borderRadius: '99px', overflow: 'visible' }}>
        <div style={{ position: 'absolute', left: '50%', top: '-1px', width: '2px', height: '8px', background: '#94a3b8', transform: 'translateX(-50%)', zIndex: 2, borderRadius: '1px' }} />
        {!isOnTrack && (
          <div style={{
            position: 'absolute', top: 0, height: '100%', background: cor, borderRadius: '99px',
            ...(isAhead ? { left: '50%', width: `${fillPct}%` } : { right: '50%', width: `${fillPct}%` })
          }} />
        )}
      </div>
    </div>
  );
};

// ─── Modal principal: Metas ───────────────────────────────────────────────────
const ModalMetas = ({ onClose }) => {
  const [metas, setMetas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [financiamentos, setFinanciamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [subModal, setSubModal] = useState(null);
  const [colapsadas, setColapsadas] = useState(new Set(['__all__'])); // começa tudo fechado
  const toggleColapso = (id) => setColapsadas(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const isAberto = (id) => !colapsadas.has(id);
  const [mesFiltro, setMesFiltro] = useState(() => {
    const h = new Date();
    return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}`;
  });
  const token = localStorage.getItem('token');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [rm, rf, rb] = await Promise.all([
        axios.get(`${API_CONFIG.API_URL}/metas`, { headers: { 'x-auth-token': token } }),
        axios.get(`${API_CONFIG.API_URL}/financiamentos`, { headers: { 'x-auth-token': token } }),
        axios.get(`${API_CONFIG.API_URL}/bancos`, { headers: { 'x-auth-token': token } })
      ]);
      if (rm.data.success) setMetas(rm.data.data);
      if (rf.data.success) setFinanciamentos(rf.data.data);
      if (rb.data.success) setBancos(rb.data.data || []);
    } catch (e) {}
    setCarregando(false);
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (dados) => {
    try {
      if (subModal?.modo === 'editar') {
        await axios.put(`${API_CONFIG.API_URL}/metas/${subModal.item._id}`, dados, { headers: { 'x-auth-token': token } });
      } else {
        await axios.post(`${API_CONFIG.API_URL}/metas`, dados, { headers: { 'x-auth-token': token } });
      }
      setSubModal(null);
      carregar();
    } catch (e) {}
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta meta?')) return;
    try {
      await axios.delete(`${API_CONFIG.API_URL}/metas/${id}`, { headers: { 'x-auth-token': token } });
      carregar();
    } catch (e) {}
  };

  // Filtrar financiamentos pagos no mês selecionado
  const [anoFiltro, mmFiltro] = mesFiltro.split('-').map(Number);
  const finsPagos = financiamentos.filter(f => {
    if (!f.data) return false;
    if (f.status !== 'Pago') return false;
    const s = typeof f.data === 'string' ? f.data : new Date(f.data).toISOString();
    const [y, m] = s.split('T')[0].split('-').map(Number);
    return y === anoFiltro && m === mmFiltro;
  });

  // Metas do mês selecionado
  const metasDoMes = metas.filter(m => !m.mes_ano || m.mes_ano === mesFiltro);

  // Financiamentos do mês (qualquer status) para Comparecimento
  const finsDoMes = financiamentos.filter(f => {
    if (!f.data) return false;
    const s = typeof f.data === 'string' ? f.data : new Date(f.data).toISOString();
    const [y, m] = s.split('T')[0].split('-').map(Number);
    return y === anoFiltro && m === mmFiltro;
  });

  const calcProgresso = (meta) => {
    const fins = finsPagos.filter(f => f.banco === meta.banco);
    const valorRealizado = fins.reduce((s, f) => s + (f.valor_financiado || 0), 0);
    const qtdRealizada = fins.length;
    return { valorRealizado, qtdRealizada };
  };

  const isAhead  = (actual, expected) => actual != null && (actual - expected) >= 2;
  const isBehind = (actual, expected) => actual != null && (actual - expected) <= -2;
  const getLabel = (actual, expected) => {
    if (actual == null) return '';
    const d = actual - expected;
    if (Math.abs(d) < 2) return '≈ em dia';
    return d > 0 ? `▲ +${d.toFixed(0)}%` : `▼ ${d.toFixed(0)}%`;
  };

  // Progresso esperado do mês (% do mês decorrido)
  const hoje = new Date();
  const mesFiltroAtual = `${hoje.getFullYear()}-${String(hoje.getMonth()+1).padStart(2,'0')}`;
  let expectedPct;
  if (mesFiltro === mesFiltroAtual) {
    const diasNoMes = new Date(anoFiltro, mmFiltro, 0).getDate();
    expectedPct = (hoje.getDate() / diasNoMes) * 100;
  } else if (mesFiltro < mesFiltroAtual) {
    expectedPct = 100; // mês passado = devia estar completo
  } else {
    expectedPct = 0; // mês futuro = nada esperado ainda
  }

  // Gerar opções de meses para o select
  const mesesMap = new Map();
  for (let i = 0; i < 12; i++) {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    mesesMap.set(val, { val, label });
  }
  metas.forEach(meta => {
    if (!meta.mes_ano || mesesMap.has(meta.mes_ano)) return;
    const d = new Date(`${meta.mes_ano}-01T00:00:00`);
    if (Number.isNaN(d.getTime())) return;
    mesesMap.set(meta.mes_ano, {
      val: meta.mes_ano,
      label: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    });
  });
  const mesesOpcoes = Array.from(mesesMap.values()).sort((a, b) => b.val.localeCompare(a.val));
  const mesesComMetas = Array.from(new Set(
    metas
      .map(meta => meta.mes_ano)
      .filter(Boolean)
  )).sort((a, b) => b.localeCompare(a));
  const mesMaisRecenteComMeta = mesesComMetas[0] || '';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#475569' }}><IconeMetas /></span>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#1e293b' }}>Metas</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select value={mesFiltro} onChange={e => setMesFiltro(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', color: '#475569', background: '#f8fafc', cursor: 'pointer', textTransform: 'capitalize' }}>
            {mesesOpcoes.map(o => (
              <option key={o.val} value={o.val} style={{ textTransform: 'capitalize' }}>{o.label}</option>
            ))}
          </select>
          <button className="btn-primary" style={{ padding: '8px 18px', fontSize: '0.88rem' }}
            onClick={() => setSubModal({ modo: 'novo' })}>
            + Nova meta
          </button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>
      </div>

      {/* Conteúdo */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 24px' }}>
        {carregando ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>Carregando...</div>
        ) : metas.length === 0 && bancos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', border: '2px dashed #e2e8f0', borderRadius: '8px', color: '#cbd5e1' }}>
            Nenhuma meta cadastrada.
          </div>
        ) : (
          <>
            {/* Card de Comparecimento */}
            {bancos.length > 0 && (() => {
              // Normaliza nome: remove sufixos entre parênteses ex: "Bradesco (novo)" → "bradesco"
              const normBanco = (nome) => (nome || '').replace(/\s*\(.*\)\s*$/i, '').trim().toLowerCase();

              // Agrupa bancos por nome normalizado
              const grupos = {};
              bancos.forEach(b => {
                const chave = normBanco(b.nome);
                if (!grupos[chave]) grupos[chave] = { label: b.nome.replace(/\s*\(.*\)\s*$/i, '').trim(), nomes: [], id: b._id };
                grupos[chave].nomes.push(b.nome);
              });

              return (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>🏦 Comparecimento dos Bancos</div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {Object.entries(grupos).map(([chave, grupo]) => {
                    const temFicha = finsDoMes.some(f => normBanco(f.banco) === chave);
                    return (
                      <label key={chave} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 12px', borderRadius: '8px', border: `1px solid ${temFicha ? '#bbf7d0' : '#e2e8f0'}`, background: temFicha ? '#f0fdf4' : '#f8fafc', cursor: 'default', userSelect: 'none' }}>
                        <input type="checkbox" checked={temFicha} readOnly style={{ accentColor: '#16a34a', width: '15px', height: '15px' }} />
                        <span style={{ fontSize: '0.85rem', fontWeight: '500', color: temFicha ? '#166634' : '#475569' }}>{grupo.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              );
            })()}

            {/* Totalizador */}
            {(() => {
              const totalValor = metasDoMes.reduce((s, m) => s + (m.meta_valor || 0), 0);
              const totalQtd = metasDoMes.reduce((s, m) => s + (m.meta_quantidade || 0), 0);
              return (totalValor > 0 || totalQtd > 0) ? (
                <div style={{ display: 'flex', gap: '24px', marginBottom: '20px', padding: '12px 18px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', flexWrap: 'wrap' }}>
                  {totalValor > 0 && (
                    <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                      Meta total de financiamento: <strong style={{ color: '#2563eb' }}>{exibirMoedaFin(totalValor)}</strong>
                    </span>
                  )}
                  {totalValor > 0 && totalQtd > 0 && <span style={{ color: '#cbd5e1' }}>|</span>}
                  {totalQtd > 0 && (
                    <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                      Quantidade mínima de financiamentos: <strong style={{ color: '#2563eb' }}>{totalQtd} ficha{totalQtd !== 1 ? 's' : ''}</strong>
                    </span>
                  )}
                </div>
              ) : null;
            })()}

          {metasDoMes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', border: '2px dashed #e2e8f0', borderRadius: '8px', color: '#94a3b8', background: '#fff' }}>
              <div style={{ fontSize: '1rem', fontWeight: '800', color: '#475569', marginBottom: '8px' }}>
                Nenhuma meta cadastrada para este mês.
              </div>
              {mesMaisRecenteComMeta && mesMaisRecenteComMeta !== mesFiltro && (
                <>
                  <div style={{ fontSize: '0.84rem', marginBottom: '14px' }}>
                    Existem metas cadastradas em outro mês. Elas não foram apagadas; estão apenas fora do filtro atual.
                  </div>
                  <button
                    type="button"
                    onClick={() => setMesFiltro(mesMaisRecenteComMeta)}
                    style={{ border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', borderRadius: '8px', padding: '8px 12px', fontWeight: '700', cursor: 'pointer' }}
                  >
                    Ver mês com metas
                  </button>
                </>
              )}
            </div>
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', alignItems: 'start' }}>
            {metasDoMes.map(meta => {
              const { valorRealizado, qtdRealizada } = calcProgresso(meta);
              const pctValor = meta.meta_valor > 0 ? (valorRealizado / meta.meta_valor) * 100 : null;
              const pctQtd = meta.meta_quantidade > 0 ? (qtdRealizada / meta.meta_quantidade) * 100 : null;
              const tudo = [pctValor, pctQtd].filter(p => p !== null);
              const metaBatida = tudo.length > 0 && tudo.every(p => p >= 100);
              return (
                <div key={meta._id} style={{ background: '#fff', border: `1px solid ${isAberto(meta._id) ? '#94a3b8' : '#e2e8f0'}`, borderRadius: '12px', overflow: 'hidden', transition: 'border-color 0.15s' }}>
                  {/* Cabeçalho clicável */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', cursor: 'pointer' }}
                    onClick={() => toggleColapso(meta._id)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8', display: 'inline-block', transform: isAberto(meta._id) ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▶</span>
                      <span style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1e293b' }}>{meta.banco}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                      <button type="button"
                        style={{ padding: '3px 7px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem', color: '#475569' }}
                        onClick={() => setSubModal({ modo: 'editar', item: { ...meta, meta_valor: meta.meta_valor != null ? formatMoedaFin(String(Math.round(meta.meta_valor * 100))) : '', meta_quantidade: meta.meta_quantidade != null ? String(meta.meta_quantidade) : '' } })}>
                        ✏️
                      </button>
                      <button type="button"
                        style={{ padding: '3px 7px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem', color: '#dc2626' }}
                        onClick={() => excluir(meta._id)}>
                        ×
                      </button>
                    </div>
                  </div>

                  {/* Progresso sempre visível */}
                  <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {pctValor !== null && (
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '2px' }}>
                          Valor: <strong style={{ color: '#334155' }}>{exibirMoedaFin(valorRealizado)}</strong>
                          <span style={{ color: '#94a3b8' }}> de </span>
                          <strong style={{ color: '#334155' }}>{exibirMoedaFin(meta.meta_valor)}</strong>
                        </div>
                        <ProgressBar pct={pctValor} />
                      </div>
                    )}
                    {pctQtd !== null && (
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '2px' }}>
                          Fichas: <strong style={{ color: '#334155' }}>{qtdRealizada}</strong>
                          <span style={{ color: '#94a3b8' }}> de </span>
                          <strong style={{ color: '#334155' }}>{meta.meta_quantidade}</strong>
                        </div>
                        <ProgressBar pct={pctQtd} />
                      </div>
                    )}
                    {metaBatida && <div style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: '700', textAlign: 'center' }}>✓ Todos os critérios atingidos!</div>}
                  </div>

                  {/* Detalhes expandidos: ritmo + recompensa */}
                  {isAberto(meta._id) && (
                    <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {pctValor !== null && <PaceBar actualPct={pctValor} expectedPct={expectedPct} />}
                      {pctQtd !== null && <PaceBar actualPct={pctQtd} expectedPct={expectedPct} />}
                      {meta.observacao && (
                        <div style={{ fontSize: '0.78rem', color: '#64748b', background: '#fef9c3', borderRadius: '6px', padding: '8px 10px', borderLeft: '3px solid #fde68a', marginTop: '4px' }}>
                          🎯 {meta.observacao}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )}
          </>
        )}
      </div>

      {subModal && (
        <SubModalMeta
          inicial={subModal.modo === 'editar' ? subModal.item : null}
          mesPadrao={mesFiltro}
          onSalvar={salvar}
          onFechar={() => setSubModal(null)}
        />
      )}
    </div>
  );
};

// ─── Ícone Notas Fiscais ────────────────────────────────────────────────────
const IconeNotasFiscais = () => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

// ─── Modal Notas Fiscais ─────────────────────────────────────────────────────
const MESES_NF = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];

const mesAnoAtual = () => {
  const hoje = new Date();
  const d = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  return { val: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) };
};

const obterMesFinanciamentosNF = (mesAnoRef = '') => {
  if (!/^\d{4}-\d{2}$/.test(mesAnoRef)) return { val: '', label: '' };
  const [ano, mes] = mesAnoRef.split('-').map(Number);
  const d = new Date(ano, mes - 2, 1);
  return {
    val: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
    label: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
  };
};

const formatarDataLocalNF = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : d.toISOString();
  const [y, m, day] = s.split('T')[0].split('-');
  return `${day}/${m}/${y}`;
};

const exibirMoedaNF = (val) =>
  val != null ? Number(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';

const ModalNotasFiscais = ({ onClose }) => {
  const [grupos, setGrupos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mesAno, setMesAno] = useState(() => mesAnoAtual().val);
  const [opcoesMes, setOpcoesMes] = useState([]);
  const [uploading, setUploading] = useState({});
  const [salvandoCheck, setSalvandoCheck] = useState({});
  const fileInputs = useRef({});
  const token = localStorage.getItem('token');
  const mesBaseFinanciamentos = obterMesFinanciamentosNF(mesAno);

  useEffect(() => {
    const lista = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
      lista.push({ val: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) });
    }
    setOpcoesMes(lista);
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await axios.get(`${API_CONFIG.API_URL}/notas-fiscais/${mesAno}`, { headers: { 'x-auth-token': token } });
      if (res.data.success) setGrupos(res.data.data || []);
    } catch (e) {}
    setCarregando(false);
  }, [mesAno, token]);

  useEffect(() => { carregar(); }, [carregar]);

  const toggleConcluida = async (grupo) => {
    const novoValor = !(grupo.nota_fiscal?.concluida);
    setSalvandoCheck(prev => ({ ...prev, [grupo.banco]: true }));
    try {
      await axios.post(`${API_CONFIG.API_URL}/notas-fiscais/${mesAno}/${encodeURIComponent(grupo.banco)}`, { concluida: novoValor }, { headers: { 'x-auth-token': token } });
      setGrupos(prev => prev.map(g => g.banco === grupo.banco ? { ...g, nota_fiscal: { ...(g.nota_fiscal || {}), concluida: novoValor } } : g));
    } catch (e) {}
    setSalvandoCheck(prev => ({ ...prev, [grupo.banco]: false }));
  };

  const handleArquivo = async (grupo, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [grupo.banco]: true }));
    try {
      const formData = new FormData();
      formData.append('arquivo', file);
      const res = await axios.post(`${API_CONFIG.API_URL}/notas-fiscais/${mesAno}/${encodeURIComponent(grupo.banco)}/upload`, formData, {
        headers: { 'x-auth-token': token }
      });
      if (res.data.success) {
        setGrupos(prev => prev.map(g => g.banco === grupo.banco ? { ...g, nota_fiscal: res.data.data } : g));
      }
    } catch (e) {
      alert(e.response?.data?.message || 'Não foi possível anexar a nota fiscal. Tente novamente.');
    }
    setUploading(prev => ({ ...prev, [grupo.banco]: false }));
    if (fileInputs.current[grupo.banco]) fileInputs.current[grupo.banco].value = '';
  };

  const removerArquivo = async (grupo, index) => {
    if (!window.confirm('Remover esta nota fiscal?')) return;
    try {
      await axios.delete(`${API_CONFIG.API_URL}/notas-fiscais/${mesAno}/${encodeURIComponent(grupo.banco)}/arquivo/${index}`, { headers: { 'x-auth-token': token } });
      setGrupos(prev => prev.map(g => g.banco === grupo.banco ? { ...g, nota_fiscal: { ...(g.nota_fiscal || {}), arquivos: g.nota_fiscal?.arquivos?.filter((_, i) => i !== index) || [] } } : g));
    } catch (e) {
      alert('Não foi possível remover o arquivo.');
    }
  };

  const totalFinanciado = grupos.reduce((s, g) => s + g.financiamentos.reduce((ss, f) => ss + (f.valor_financiado || 0), 0), 0);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#475569' }}><IconeNotasFiscais /></span>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#1e293b' }}>Notas Fiscais</h2>
            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
              NF de <strong style={{ textTransform: 'capitalize' }}>{opcoesMes.find(o => o.val === mesAno)?.label || mesAno}</strong> baseada nos financiamentos de <strong style={{ textTransform: 'capitalize' }}>{mesBaseFinanciamentos.label}</strong>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', color: '#475569' }}>Total: <strong style={{ color: '#16a34a' }}>{exibirMoedaNF(totalFinanciado)}</strong></span>
          <select value={mesAno} onChange={e => setMesAno(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', color: '#475569', background: '#f8fafc', cursor: 'pointer', textTransform: 'capitalize' }}>
            {opcoesMes.map(o => <option key={o.val} value={o.val} style={{ textTransform: 'capitalize' }}>{o.label}</option>)}
          </select>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        {carregando ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>Carregando...</div>
        ) : grupos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', border: '2px dashed #e2e8f0', borderRadius: '8px', color: '#cbd5e1' }}>
            Nenhum financiamento encontrado em {mesBaseFinanciamentos.label || 'mês anterior'} para gerar NF de {opcoesMes.find(o => o.val === mesAno)?.label || mesAno}.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {grupos.map(grupo => {
              const totalBanco = grupo.financiamentos.reduce((s, f) => s + (f.valor_financiado || 0), 0);
              const totalRetornoLiqBanco = grupo.financiamentos.reduce((s, f) => {
                const ret = f.percentual_retorno && f.valor_financiado ? f.valor_financiado * (f.percentual_retorno / 100) * 0.70 : 0;
                return s + ret;
              }, 0);
              const concluida = !!grupo.nota_fiscal?.concluida;
              const arquivoUrl = grupo.nota_fiscal?.arquivo_url;
              const arquivoNome = grupo.nota_fiscal?.arquivo_nome;
              return (
                <div key={grupo.banco} style={{ background: '#fff', borderRadius: '12px', border: `1px solid ${concluida ? '#86efac' : '#e2e8f0'}`, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: concluida ? '#f0fdf4' : '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
                        <input
                          type="checkbox"
                          checked={concluida}
                          onChange={() => toggleConcluida(grupo)}
                          disabled={salvandoCheck[grupo.banco]}
                          style={{ width: '18px', height: '18px', accentColor: '#16a34a', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.95rem', fontWeight: '700', color: concluida ? '#166534' : '#1e293b' }}>{grupo.banco}</span>
                      </label>
                      {concluida && <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: '999px' }}>NF CONCLUÍDA</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <span style={{ fontSize: '0.8rem', color: '#475569' }}>{grupo.financiamentos.length} financiamento{grupo.financiamentos.length !== 1 ? 's' : ''}</span>
                      <span style={{ fontSize: '0.8rem', color: '#475569' }}>Total: <strong style={{ color: '#16a34a' }}>{exibirMoedaNF(totalBanco)}</strong></span>
                      {totalRetornoLiqBanco > 0 && (
                        <span style={{ fontSize: '0.8rem', color: '#475569' }}>Retorno líq.: <strong style={{ color: '#7c3aed' }}>{exibirMoedaNF(totalRetornoLiqBanco)}</strong></span>
                      )}
                    </div>
                  </div>

                  <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600' }}>Notas fiscais:</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {(grupo.nota_fiscal?.arquivos || []).map((arq, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#e0e7ff', padding: '3px 8px', borderRadius: '4px', fontSize: '0.78rem' }}>
                            <a href={arq.url.startsWith('http') ? arq.url : `${API_CONFIG.BASE_URL.replace('/api', '')}${arq.url}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1e3a8a', textDecoration: 'none' }}>{arq.nome}</a>
                            <button onClick={() => removerArquivo(grupo, idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 'bold' }}>X</button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        ref={el => fileInputs.current[grupo.banco] = el}
                        onChange={e => handleArquivo(grupo, e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                      <button
                        onClick={() => fileInputs.current[grupo.banco]?.click()}
                        disabled={uploading[grupo.banco]}
                        style={{ padding: '6px 14px', fontSize: '0.8rem', fontWeight: '600', color: '#fff', background: uploading[grupo.banco] ? '#94a3b8' : '#2563eb', border: 'none', borderRadius: '6px', cursor: uploading[grupo.banco] ? 'not-allowed' : 'pointer' }}
                      >
                        {uploading[grupo.banco] ? 'Enviando...' : 'Anexar NF'}
                      </button>
                    </div>
                  </div>

                  <div style={{ padding: '0 18px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          {['Data', 'Vendedor', 'Cliente', 'CPF', 'Placa', 'Veículo', 'Valor financiado', 'Retorno líq.'].map(h => (
                            <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: '0.7rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.financiamentos.map(f => {
                          const retornoLiq = f.percentual_retorno && f.valor_financiado ? f.valor_financiado * (f.percentual_retorno / 100) * 0.70 : null;
                          return (
                          <tr key={f._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#475569', whiteSpace: 'nowrap' }}>{formatarDataLocalNF(f.data)}</td>
                            <td style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#334155' }}>{f.vendedor || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#334155' }}>{f.cliente_nome || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: '0.82rem', fontFamily: 'monospace', color: '#475569' }}>{f.cliente_cpf || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: '0.82rem', fontFamily: 'monospace', color: '#475569' }}>{f.placa || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#334155' }}>{f.veiculo || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: '0.82rem', fontWeight: '600', color: '#16a34a', whiteSpace: 'nowrap' }}>{exibirMoedaNF(f.valor_financiado)}</td>
                            <td style={{ padding: '10px 12px', fontSize: '0.82rem', fontWeight: '600', color: '#7c3aed', whiteSpace: 'nowrap' }}>{retornoLiq ? exibirMoedaNF(retornoLiq) : '—'}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Página Análise ──────────────────────────────────────────────────────────
const Analise = ({ onToggleSidebar, assuntoAtivo, onAssuntoChange }) => {
  const [modalAberto, setModalAberto] = useState(assuntoAtivo || null);

  useEffect(() => {
    setModalAberto(assuntoAtivo || null);
  }, [assuntoAtivo]);

  const abrirAssunto = (assunto) => {
    setModalAberto(assunto);
    onAssuntoChange?.(assunto);
  };

  const fecharAssunto = () => {
    setModalAberto(null);
    onAssuntoChange?.(null);
  };

  return (
    <div className="content-section analise-page">
      <div className="page-header-mobile">
        <button className="mobile-menu-toggle" onClick={onToggleSidebar}>☰</button>
        <h1>Análise</h1>
      </div>

      <div className="analise-tool-groups">
        <section className="analise-tool-group" aria-labelledby="grupo-financiamentos">
          <div className="analise-tool-group-header">
            <h2 id="grupo-financiamentos">Financiamentos</h2>
          </div>
          <div className="analise-tools-grid">
            <ToolCard label="Cotação" icon={<IconeCotacao />} onClick={() => abrirAssunto('cotacao')} />
            <ToolCard label="Financiamentos" icon={<IconeFinanciamentos />} onClick={() => abrirAssunto('financiamentos')} />
            <ToolCard label="Notas Fiscais" icon={<IconeNotasFiscais />} onClick={() => abrirAssunto('notasfiscais')} />
          </div>
        </section>

        <section className="analise-tool-group" aria-labelledby="grupo-performance">
          <div className="analise-tool-group-header">
            <h2 id="grupo-performance">Performance</h2>
          </div>
          <div className="analise-tools-grid">
            <ToolCard label="Metas" icon={<IconeMetas />} onClick={() => abrirAssunto('metas')} />
            <ToolCard label="Ranking" icon={<IconeRanking />} onClick={() => abrirAssunto('ranking')} />
          </div>
        </section>
      </div>

      {modalAberto === 'cotacao' && <ModalCotacao onClose={fecharAssunto} />}
      {modalAberto === 'financiamentos' && <ModalFinanciamentos onClose={fecharAssunto} />}
      {modalAberto === 'notasfiscais' && <ModalNotasFiscais onClose={fecharAssunto} />}
      {modalAberto === 'metas' && <ModalMetas onClose={fecharAssunto} />}
      {modalAberto === 'ranking' && <ModalRanking onClose={fecharAssunto} />}
    </div>
  );
};

export default Analise;
